/**
 * The client-graph half of the flag facade's read contract.
 *
 * Both hooks are **synchronous** — they return a value, never a query result —
 * because the facade resolves flags from a snapshot that is already present:
 * the manifest in the client bundle, replaced during hydration by the one the
 * SSR pass resolved (`routes/__root.tsx`). There is no fetch to wait on today
 * and none once PostHog backs it (ADR-0002 bootstraps the values), so no
 * consumer should carry a loading branch for a flag.
 *
 * They own no query key: the facade is the source, so a React Query entry
 * beside it would be a second answer to the same question — the reason
 * `usePermissions` has none either (client/docs/architecture.md, L5).
 */

import { useSyncExternalStore } from "react";

import {
  featureFlag,
  featureFlagPayload,
  subscribeToFeatureFlags,
  type BffBooleanFeatureFlagKey,
  type BffFeatureFlagPayloads,
  type BffPayloadFeatureFlagKey,
} from "@/lib/feature-flags";

/**
 * Read a flag, re-rendering if the snapshot changes under a mounted consumer.
 *
 * The same function serves as the server snapshot: SSR and hydration read the
 * store the bootstrap has already filled, so the two renders agree.
 */
export function useFeatureFlag(key: BffBooleanFeatureFlagKey): boolean {
  const read = () => featureFlag(key);
  return useSyncExternalStore(subscribeToFeatureFlags, read, read);
}

/** Read a flag's payload, or `undefined` when the key carries none. */
export function useFeatureFlagPayload<Key extends BffPayloadFeatureFlagKey>(
  key: Key,
): BffFeatureFlagPayloads[Key] | undefined {
  const read = () => featureFlagPayload(key);
  return useSyncExternalStore(subscribeToFeatureFlags, read, read);
}
